import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import request, { ApiError } from '../utils/request';

interface Organization {
  id: string;
  name: string;
  type: string;
  role: string;
}

interface Project {
  id: string;
  name: string;
  organization_id: string;
  role: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  organizations: Organization[];
  projects: Project[];
}

interface AuthState {
  // State
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  currentProject: Project | null;

  // Methods
  login: (
    email: string,
    password: string,
    acceptedTosVersion?: string
  ) => Promise<{ success: boolean; message?: string; code?: number; data?: unknown }>;
  logout: () => void;
  checkAuth: () => boolean;
  setToken: (token: string) => void;
  fetchUserInfo: () => Promise<{ success: boolean; message?: string }>;
  initializeAuth: () => Promise<{ success: boolean; message?: string }>;
  setCurrentProject: (project: Project | null) => void;
}

// User portal login API
// POST /grnt/user/login
const userLoginApi = async (
  email: string,
  password: string,
  acceptedTosVersion?: string
): Promise<{
  success: boolean;
  token?: string;
  user?: User;
  message?: string;
  code?: number;
  data?: unknown;
}> => {
  try {
    const response = await request.post('/grnt/user/login', {
      email,
      password,
      accepted_tos_version: acceptedTosVersion || '',
    });

    return {
      success: true,
      token: response.data.token,
      user: {
        id: String(response.data.user.id),
        email: response.data.user.email,
        name: response.data.user.name,
        organizations: response.data.user.organizations || [],
        projects: response.data.user.projects || [],
      },
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        code: error.code,
        data: error.data,
      };
    }
    const errorMessage = error instanceof Error ? error.message : 'Login failed, please try again';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

// User portal logout API
// POST /gruser/logout
const userLogoutApi = async (): Promise<{ success: boolean; message?: string }> => {
  try {
    await request.post('/gruser/logout');
    return { success: true };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Logout failed';
    return { success: false, message: errorMessage };
  }
};

// Get current user info API
// GET /gruser/me
const userGetMeApi = async (): Promise<{ success: boolean; user?: User; message?: string }> => {
  try {
    const response = await request.get('/gruser/me');
    return {
      success: true,
      user: {
        id: String(response.data.id),
        email: response.data.email,
        name: response.data.name,
        organizations: response.data.organizations || [],
        projects: response.data.projects || [],
      },
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user info';
    return { success: false, message: errorMessage };
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      currentProject: null,

      login: async (email: string, password: string, acceptedTosVersion?: string) => {
        set({ isLoading: true });

        try {
          const result = await userLoginApi(email, password, acceptedTosVersion);

          if (result.success && result.token && result.user) {
            // Validate and restore current project after login
            const { currentProject } = get();
            const validatedProject = validateAndRestoreProject(
              currentProject,
              result.user.projects
            );

            set({
              token: result.token,
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
              currentProject: validatedProject,
            });
            return { success: true };
          } else {
            // Login failed — clear auth state to prevent auto-redirect by PublicRoute
            set({
              token: null,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              currentProject: null,
            });
            return {
              success: false,
              message: result.message,
              code: result.code,
              data: result.data,
            };
          }
        } catch {
          // Login exception — clear auth state
          set({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            currentProject: null,
          });
          return { success: false, message: 'Login failed, please try again' };
        }
      },

      logout: async () => {
        // Call backend logout endpoint to invalidate the server-side token
        await userLogoutApi();
        // Clear local state, but keep currentProject so it can be restored on next login
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        });
      },

      checkAuth: () => {
        const { token } = get();
        if (!token) {
          return false;
        }
        return true;
      },

      setToken: (token: string) => {
        set({
          token,
          isAuthenticated: true,
        });
      },

      fetchUserInfo: async () => {
        set({ isLoading: true });

        try {
          const result = await userGetMeApi();

          if (result.success && result.user) {
            // Validate and restore current project after fetching user info
            const { currentProject } = get();
            const validatedProject = validateAndRestoreProject(
              currentProject,
              result.user.projects
            );

            set({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
              currentProject: validatedProject,
            });
            return { success: true };
          } else {
            // Failed to fetch user info — clear auth state
            set({
              token: null,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              currentProject: null,
            });
            return { success: false, message: result.message };
          }
        } catch {
          // Exception while fetching user info — clear auth state
          set({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            currentProject: null,
          });
          return { success: false, message: 'Failed to fetch user info, please try again' };
        }
      },

      initializeAuth: async () => {
        const { token, fetchUserInfo } = get();

        // If a token exists, fetch the latest user info
        if (isValidToken(token)) {
          const result = await fetchUserInfo();
          return result;
        }
        return { success: false, message: 'Invalid token' };
      },

      setCurrentProject: (project: Project | null) => {
        set({ currentProject: project });
      },
    }),
    {
      name: 'user-auth-storage', // localStorage key
      partialize: state => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentProject: state.currentProject,
      }),
    }
  )
);

// Helper: validate token
export const isValidToken = (token: string | null): boolean => {
  if (!token) return false;
  // Token length check (backend generates a 64-char random string)
  return token.length >= 64;
};

// Helper: validate and restore current project
// Checks if the cached project still belongs to the user, otherwise selects the first available project
export const validateAndRestoreProject = (
  currentProject: Project | null,
  userProjects: Project[]
): Project | null => {
  if (!currentProject || userProjects.length === 0) {
    return userProjects.length > 0 ? userProjects[0] : null;
  }

  // Check if the cached project still exists in user's projects
  const projectExists = userProjects.some(p => p.id === currentProject.id);
  if (projectExists) {
    return currentProject;
  }

  // Cached project no longer belongs to user, select the first available project
  return userProjects.length > 0 ? userProjects[0] : null;
};

// Helper: check if user has a specific permission code
